import { useQuery } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Download } from 'lucide-react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { RevisionBadge } from './shared';

GlobalWorkerOptions.workerSrc = workerSrc;

const DxfViewer = lazy(() => import('./DxfViewer'));
const IfcViewer = lazy(() => import('./IfcViewer'));

interface FileInfo {
  id: string;
  name: string;
  ext: string;
  sizeBytes: number;
  setId: string;
  setCode: string;
  projectId: string;
  projectCode: string;
  revisionId: string;
  revision: string;
  revisionStatus: string;
  dwgReady: boolean;
}

/** Чертёж DXF, DWG (переводом в DXF на сервере) и модель IFC во встроенном просмотре (ПД-03) */
export function FileViewerPage() {
  const { fileId } = useParams({ strict: false }) as { fileId: string };
  const q = useQuery({
    queryKey: ['psd-file', fileId],
    queryFn: () => api<FileInfo>(`/psd/files/${fileId}/info`),
  });
  useCrumbs(
    q.data
      ? [
          { label: q.data.projectCode, to: `/psd?project=${q.data.projectId}` },
          { label: q.data.setCode, to: `/psd/sets/${q.data.setId}` },
          { label: q.data.name },
        ]
      : null,
    q.error,
  );
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const f = q.data;
  const viewUrl = `/api/v1/psd/files/${f.id}?view=1`;
  return (
    <Page>
      <PageHeader
        title={<span className="[overflow-wrap:anywhere]">{f.name}</span>}
        subtitle={`${f.setCode}, ревизия ${f.revision}`}
        actions={
          <a
            href={`/api/v1/psd/files/${f.id}?download=1`}
            className="inline-flex h-12 items-center gap-2 rounded-md border border-line-strong px-5 text-[15px] text-ink hover:underline"
          >
            <Download className="size-4" /> Скачать
          </a>
        }
      >
        <div className="mt-2">
          <RevisionBadge status={f.revisionStatus} />
        </div>
      </PageHeader>
      <Panel bodyClassName="p-0">
        <Suspense fallback={<Spinner label="Открываем просмотр" />}>
          {f.ext === 'dxf' ? (
            <DxfViewer url={viewUrl} />
          ) : f.ext === 'ifc' ? (
            <IfcViewer url={viewUrl} />
          ) : f.ext === 'dwg' && f.dwgReady ? (
            <>
              <p className="border-b border-line px-4 py-2 text-[13px] text-ink-2">
                Чертёж DWG переведён на сервере в DXF для просмотра. Подлинник — кнопка «Скачать».
              </p>
              <DxfViewer url={viewUrl} />
            </>
          ) : f.ext === 'dwg' ? (
            <div className="p-5">
              <EmptyState title="Этот чертёж DWG открывается скачиванием">
                Просмотр DWG — для файлов до 100 МБ на сервере с программой перевода. Тот же чертёж в DXF
                откроется здесь со слоями и измерениями.
              </EmptyState>
            </div>
          ) : (
            <div className="p-5">
              <EmptyState title="Этот файл открывается скачиванием">
                Встроенный просмотр — для PDF, DXF и IFC.
              </EmptyState>
            </div>
          )}
        </Suspense>
      </Panel>
    </Page>
  );
}

/* ───────────────────────── Сравнение ревизий листа (ПД-06) ───────────────────────── */

interface SheetView {
  id: string;
  number: string;
  name: string;
  setId: string;
  setCode: string;
  projectId: string;
  projectCode: string;
  versions: Array<{
    revisionId: string;
    revision: string;
    status: string;
    fileId: string | null;
    fileName: string | null;
    page: number | null;
  }>;
}

/** Предел площади листа для сравнения: холст больше ~16 Мпикс телефон не создаст, памяти хватит на три таких */
const MAX_COMPARE_PIXELS = 8_000_000;

async function renderPage(url: string, page: number, width: number): Promise<ImageData> {
  const doc = await getDocument({ url, withCredentials: true }).promise;
  try {
    const pg = await doc.getPage(Math.min(page, doc.numPages));
    const base = pg.getViewport({ scale: 1 });
    let scale = width / base.width;
    if (base.width * base.height * scale * scale > MAX_COMPARE_PIXELS)
      scale = Math.sqrt(MAX_COMPARE_PIXELS / (base.width * base.height));
    const vp = pg.getViewport({ scale });
    const c = document.createElement('canvas');
    c.width = Math.floor(vp.width);
    c.height = Math.floor(vp.height);
    const g = c.getContext('2d', { willReadFrequently: true });
    if (!g) throw new Error('Лист слишком большой для сравнения на этом устройстве');
    g.fillStyle = '#fff';
    g.fillRect(0, 0, c.width, c.height);
    await pg.render({ canvasContext: g, viewport: vp }).promise;
    return g.getImageData(0, 0, c.width, c.height);
  } finally {
    void doc.destroy();
  }
}

/**
 * Сравнение двух ревизий листа (ПД-06, ПД-31): наложение (что появилось — зелёным, что исчезло — красным,
 * общее — серым), два листа рядом с общей прокруткой и шторка, которую тянут влево-вправо. Листы приводятся
 * к одной ширине; сравнение по пикселям в браузере, файлы никуда не уходят.
 */
type CompareMode = 'overlay' | 'side' | 'slider';
const MODES: Array<{ value: CompareMode; label: string }> = [
  { value: 'overlay', label: 'Наложение' },
  { value: 'side', label: 'Рядом' },
  { value: 'slider', label: 'Шторка' },
];
const MODE_KEY = 'psd-compare-mode';
export function SheetComparePage() {
  const { sheetId } = useParams({ strict: false }) as { sheetId: string };
  const search = useSearch({ strict: false }) as { a?: string; b?: string };
  const q = useQuery({
    queryKey: ['psd-sheet', sheetId],
    queryFn: () => api<SheetView>(`/psd/sheets/${sheetId}`),
  });
  const withFile = (q.data?.versions ?? []).filter(
    (v) => v.fileId && v.fileName?.toLowerCase().endsWith('.pdf'),
  );
  const [a, setA] = useState<string | undefined>(search.a);
  const [b, setB] = useState<string | undefined>(search.b);
  const older = withFile.find((v) => v.revisionId === a) ?? withFile[1];
  const newer = withFile.find((v) => v.revisionId === b) ?? withFile[0];
  const canvas = useRef<HTMLCanvasElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [stats, setStats] = useState<{ added: number; removed: number; sameShape: boolean } | null>(null);
  const [imgs, setImgs] = useState<{ oa: ImageData; nb: ImageData } | null>(null);
  // Выбранный вид запоминается в браузере
  const [mode, setModeState] = useState<CompareMode>(() => {
    try {
      const v = localStorage.getItem(MODE_KEY);
      return v === 'side' || v === 'slider' ? v : 'overlay';
    } catch {
      return 'overlay';
    }
  });
  const setMode = (m: CompareMode) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      // браузер не даёт сохранить — вид просто не запомнится
    }
  };
  const [cut, setCut] = useState(50);
  const oldCanvas = useRef<HTMLCanvasElement>(null);
  const newCanvas = useRef<HTMLCanvasElement>(null);
  const oldBox = useRef<HTMLDivElement>(null);
  const newBox = useRef<HTMLDivElement>(null);
  // Два листа рядом прокручиваются вместе — от той рамки, которую трогает человек (палец, мышь, колесо);
  // прокрутка второй рамки обратно не передаётся, поэтому при разной высоте листов оба долистываются до конца
  const active = useRef<'old' | 'new' | null>(null);
  const sync = (side: 'old' | 'new') => {
    if (active.current && active.current !== side) return;
    const from = side === 'old' ? oldBox.current : newBox.current;
    const to = side === 'old' ? newBox.current : oldBox.current;
    if (!from || !to) return;
    to.scrollTop = from.scrollTop;
    to.scrollLeft = from.scrollLeft;
  };
  useCrumbs(
    q.data
      ? [
          { label: q.data.setCode, to: `/psd/sets/${q.data.setId}` },
          { label: `Лист ${q.data.number}`, to: `/psd/sheets/${q.data.id}` },
          { label: 'Сравнение ревизий' },
        ]
      : null,
    q.error,
  );

  useEffect(() => {
    setStats(null);
    setImgs(null);
    if (!older?.fileId || !newer?.fileId || older.revisionId === newer.revisionId || !canvas.current) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    const width = Math.min(2400, Math.max(1200, (canvas.current.parentElement?.clientWidth ?? 600) * 2));
    Promise.all([
      renderPage(`/api/v1/psd/files/${older.fileId}`, older.page ?? 1, width),
      renderPage(`/api/v1/psd/files/${newer.fileId}`, newer.page ?? 1, width),
    ])
      .then(([oa, nb]) => {
        if (cancelled || !canvas.current) return;
        const w = Math.min(oa.width, nb.width);
        const h = Math.min(oa.height, nb.height);
        const c = canvas.current;
        c.width = w;
        c.height = h;
        const g = c.getContext('2d');
        if (!g) throw new Error('Лист слишком большой для сравнения на этом устройстве');
        const out = g.createImageData(w, h);
        let added = 0;
        let removed = 0;
        const dark = (d: Uint8ClampedArray, i: number) => d[i]! + d[i + 1]! + d[i + 2]! < 600;
        for (let y = 0; y < h; y++)
          for (let x = 0; x < w; x++) {
            const io = (y * oa.width + x) * 4;
            const ib = (y * nb.width + x) * 4;
            const o = (y * w + x) * 4;
            const inOld = dark(oa.data, io);
            const inNew = dark(nb.data, ib);
            let cr = 255;
            let cg = 255;
            let cb = 255;
            if (inOld && inNew) cr = cg = cb = 170;
            else if (inNew) {
              cr = 7;
              cg = 148;
              cb = 85;
              added++;
            } else if (inOld) {
              cr = 217;
              cg = 45;
              cb = 32;
              removed++;
            }
            out.data[o] = cr;
            out.data[o + 1] = cg;
            out.data[o + 2] = cb;
            out.data[o + 3] = 255;
          }
        g.putImageData(out, 0, 0);
        setImgs({ oa, nb });
        const sameShape = Math.abs(oa.width / oa.height - nb.width / nb.height) < 0.01;
        setStats({ added, removed, sameShape });
      })
      .catch((e: unknown) => !cancelled && setError(e))
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, [older?.fileId, newer?.fileId, older?.page, newer?.page, older?.revisionId, newer?.revisionId]);

  // Листы «было» и «стало» для видов «рядом» и «шторка»
  useEffect(() => {
    if (!imgs || mode === 'overlay') return;
    for (const [c, d] of [
      [oldCanvas.current, imgs.oa],
      [newCanvas.current, imgs.nb],
    ] as const) {
      if (!c) continue;
      c.width = d.width;
      c.height = d.height;
      c.getContext('2d')?.putImageData(d, 0, 0);
    }
  }, [imgs, mode]);

  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const s = q.data;
  const same = older?.revisionId === newer?.revisionId;
  return (
    <Page>
      <PageHeader title={`Лист ${s.number}: сравнение ревизий`} subtitle={`${s.name} · ${s.setCode}`} />
      {withFile.length < 2 ? (
        <EmptyState title="Сравнивать пока нечего">
          Для сравнения нужны две ревизии листа с файлом PDF.
        </EmptyState>
      ) : (
        <Panel bodyClassName="p-0">
          <div className="flex flex-col gap-2 border-b border-line px-4 py-3 md:flex-row md:items-center">
            <span className="flex items-center gap-2 text-[13px]">
              Было
              <Select
                aria-label="Было"
                className="h-11 md:h-10 md:w-48"
                value={older?.revisionId}
                onChange={(e) => setA(e.target.value)}
              >
                {withFile.map((v) => (
                  <option key={v.revisionId} value={v.revisionId}>
                    {v.revision}
                  </option>
                ))}
              </Select>
            </span>
            <span className="flex items-center gap-2 text-[13px]">
              Стало
              <Select
                aria-label="Стало"
                className="h-11 md:h-10 md:w-48"
                value={newer?.revisionId}
                onChange={(e) => setB(e.target.value)}
              >
                {withFile.map((v) => (
                  <option key={v.revisionId} value={v.revisionId}>
                    {v.revision}
                  </option>
                ))}
              </Select>
            </span>
            <div
              role="tablist"
              aria-label="Вид сравнения"
              className="flex rounded-md border border-line p-0.5"
            >
              {MODES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  role="tab"
                  aria-selected={mode === m.value}
                  onClick={() => setMode(m.value)}
                  className={clsx(
                    'h-11 flex-1 rounded px-3 text-[13px] font-medium md:h-9',
                    mode === m.value ? 'bg-ink text-surface' : 'text-ink-2 hover:text-ink',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <span
              className={clsx('flex flex-wrap gap-3 text-[13px] md:ml-auto', mode !== 'overlay' && 'hidden')}
            >
              <span className="flex items-center gap-1">
                <span className="inline-block size-3 rounded-sm bg-[#079455]" /> добавлено
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block size-3 rounded-sm bg-[#D92D20]" /> удалено
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block size-3 rounded-sm bg-[#AAAAAA]" /> без изменений
              </span>
            </span>
          </div>
          {older?.revisionId === newer?.revisionId ? (
            <p className="px-4 py-6 text-[14px] text-ink-2">Выберите две разные ревизии.</p>
          ) : null}
          {error ? <ErrorBox error={error} className="m-3" /> : null}
          {busy ? <Spinner label="Сравниваем листы" /> : null}
          {stats && !busy && !stats.sameShape ? (
            <p className="px-4 pt-3 text-[13px] text-warning">
              Листы разного формата — сравнивается общая часть от левого верхнего угла.
            </p>
          ) : null}
          {stats && !busy ? (
            <p className="px-4 pt-3 text-[13px] text-ink-2">
              {stats.added + stats.removed === 0
                ? 'Листы совпадают.'
                : `Изменённых точек: добавлено ${stats.added.toLocaleString('ru-RU')}, удалено ${stats.removed.toLocaleString('ru-RU')}. Список изменений словами подготовит Pilot (этап 12) — проверяет ПТО.`}
            </p>
          ) : null}
          <div className={clsx('max-h-[75dvh] overflow-auto p-2', (same || mode !== 'overlay') && 'hidden')}>
            <canvas ref={canvas} className="block h-auto w-full bg-white" />
          </div>
          {mode === 'side' && imgs && !same ? (
            <div className="grid gap-2 p-2 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <span className="text-[13px] text-ink-2">Было: {older?.revision}</span>
                <div
                  ref={oldBox}
                  onScroll={() => sync('old')}
                  onPointerDown={() => {
                    active.current = 'old';
                  }}
                  onWheel={() => {
                    active.current = 'old';
                  }}
                  onTouchStart={() => {
                    active.current = 'old';
                  }}
                  className="max-h-[38dvh] overflow-auto rounded border border-line md:max-h-[70dvh]"
                >
                  <canvas ref={oldCanvas} className="block h-auto w-[200%] max-w-none bg-white md:w-[160%]" />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[13px] text-ink-2">Стало: {newer?.revision}</span>
                <div
                  ref={newBox}
                  onScroll={() => sync('new')}
                  onPointerDown={() => {
                    active.current = 'new';
                  }}
                  onWheel={() => {
                    active.current = 'new';
                  }}
                  onTouchStart={() => {
                    active.current = 'new';
                  }}
                  className="max-h-[38dvh] overflow-auto rounded border border-line md:max-h-[70dvh]"
                >
                  <canvas ref={newCanvas} className="block h-auto w-[200%] max-w-none bg-white md:w-[160%]" />
                </div>
              </div>
            </div>
          ) : null}
          {mode === 'slider' && imgs && !same ? (
            <div className="flex flex-col gap-2 p-2">
              <label className="flex items-center gap-3 text-[13px] text-ink-2">
                <span className="shrink-0">Стало {newer?.revision}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={cut}
                  onChange={(e) => setCut(Number(e.target.value))}
                  aria-label="Шторка: слева — новый лист, справа — прежний"
                  className="h-11 w-full accent-[#079455]"
                />
                <span className="shrink-0">Было {older?.revision}</span>
              </label>
              <div className="max-h-[75dvh] overflow-auto rounded border border-line">
                <div
                  className="relative bg-white"
                  // Высота — по более высокому листу: при разном формате оба видны целиком
                  style={{
                    aspectRatio: `1 / ${Math.max(imgs.oa.height / imgs.oa.width, imgs.nb.height / imgs.nb.width)}`,
                  }}
                >
                  <canvas ref={oldCanvas} className="absolute top-0 left-0 block h-auto w-full bg-white" />
                  <canvas
                    ref={newCanvas}
                    className="absolute top-0 left-0 block h-auto w-full bg-white"
                    style={{ clipPath: `inset(0 ${100 - cut}% 0 0)` }}
                  />
                  <div
                    className="pointer-events-none absolute inset-y-0 w-0.5 bg-[#079455]"
                    style={{ left: `${cut}%` }}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </Panel>
      )}
    </Page>
  );
}
